import type { ReactNode } from 'react';
import type { FinlandRegionCode } from '../../../utils/map/finland-region-geometry';
import { cx } from '../cx';
import { heatSteps, stepOf } from './Heatmap';
import { NARROW_ONLY, WIDE_ONLY, type ChartLayout } from './layout';
import { CHART_MINIMUM, LABEL_TEXT, SEQ_FILL, SEQ_TEXT, VALUE_TEXT, percentOf } from './scale';

/**
 * Finland's 19 regions as equal tiles in their rough positions (column, row on a 5 by 7 grid),
 * reviewed once and fixed: Lappi on top, Uusimaa at the bottom, Åland in its own tile in the
 * south-west corner.
 */
export const TILE_LAYOUT: Record<FinlandRegionCode, { col: number; row: number }> = {
  '19': { col: 2, row: 0 },
  '17': { col: 2, row: 1 },
  '18': { col: 3, row: 1 },
  '16': { col: 1, row: 2 },
  '11': { col: 3, row: 2 },
  '12': { col: 4, row: 2 },
  '15': { col: 0, row: 3 },
  '14': { col: 1, row: 3 },
  '13': { col: 2, row: 3 },
  '10': { col: 3, row: 3 },
  '04': { col: 0, row: 4 },
  '06': { col: 1, row: 4 },
  '07': { col: 2, row: 4 },
  '08': { col: 3, row: 4 },
  '09': { col: 4, row: 4 },
  '02': { col: 0, row: 5 },
  '05': { col: 1, row: 5 },
  '01': { col: 2, row: 5 },
  '21': { col: 0, row: 6 },
};

export type RegionTile = {
  code: FinlandRegionCode;
  /** The region's name in the page's language. */
  name: ReactNode;
  /** A rate (per 1,000 residents, a share), never a raw count. */
  value: number;
  display: ReactNode;
  /** How many ads the rate rests on; under 10 the tile names the region and says too few. */
  sample: number;
};

export type TileMapProps = {
  tiles: RegionTile[];
  /** The tile the story is about: ink ring. */
  highlightCode?: FinlandRegionCode;
  /** Five ascending boundaries for the six ramp steps; defaults to round numbers from the data. */
  steps?: number[];
  tooFew: ReactNode;
  /**
   * `auto` (default) switches to the narrow form in a narrow container: the regions as a ranked
   * list, most first, each with its ramp colour, since 19 tiles across a phone are too small to name.
   */
  layout?: ChartLayout;
  /** One sentence with the top regions and their values, for screen readers. */
  description: string;
  className?: string;
};

/**
 * Every region the same size, so large empty regions do not dominate and small busy ones are not
 * lost: for rates by region, coloured by the sequential ramp, each tile naming its region and its
 * value. Thin regions are labelled, not coloured. For towns use BubbleMap. Long names hyphenate by
 * the page's `lang`.
 */
export function TileMap({
  tiles,
  highlightCode,
  steps,
  tooFew,
  layout = 'auto',
  description,
  className,
}: TileMapProps) {
  const shown = tiles.filter((t) => t.sample >= CHART_MINIMUM.cell);
  const bounds = steps ?? heatSteps(shown.map((t) => t.value));
  const max = Math.max(1, ...shown.map((t) => t.value));
  const ranked = [...shown].sort((a, b) => b.value - a.value);
  const thin = tiles.filter((t) => t.sample < CHART_MINIMUM.cell);
  return (
    <div className={cx('@container', className)}>
      <div
        role="img"
        aria-label={description}
        className={cx('grid max-w-xl grid-cols-5 gap-1', WIDE_ONLY[layout])}
        style={{ gridTemplateRows: 'repeat(7, minmax(3rem, auto))' }}
      >
        {tiles.map((t) => {
          const place = TILE_LAYOUT[t.code];
          const ok = t.sample >= CHART_MINIMUM.cell;
          const s = ok ? stepOf(t.value, bounds) : 0;
          return (
            <span
              key={t.code}
              className={cx(
                'flex min-w-0 flex-col justify-between rounded-jc-info p-2',
                ok ? cx(SEQ_FILL[s], SEQ_TEXT[s]) : 'bg-jc-elevated text-jc-ink-secondary',
                t.code === highlightCode && 'ring-2 ring-jc-ink ring-inset',
              )}
              style={{ gridColumn: place.col + 1, gridRow: place.row + 1 }}
            >
              <span className="line-clamp-2 text-xs leading-tight font-semibold break-words hyphens-auto">
                {t.name}
              </span>
              {ok ? (
                <span className="type-subheading tabular-nums">{t.display}</span>
              ) : (
                <span className="text-xs leading-tight">{tooFew}</span>
              )}
            </span>
          );
        })}
      </div>
      {/* The narrow form: the same regions ranked, each keyed to its tile colour. */}
      <div role="img" aria-label={description} className={cx('flex flex-col gap-1.5', NARROW_ONLY[layout])}>
        {ranked.map((t) => {
          const hl = t.code === highlightCode;
          return (
            <span key={t.code} className="flex items-center gap-2">
              <span className={cx('size-3 shrink-0 rounded-jc-info', SEQ_FILL[stepOf(t.value, bounds)])} />
              <span className={cx('w-2/5 shrink-0 truncate', LABEL_TEXT, hl && 'font-semibold text-jc-ink')}>
                {t.name}
              </span>
              <span className="flex min-w-0 flex-1 items-center gap-2">
                <span
                  className={cx('block h-2.5 shrink-0 rounded-jc-info', hl ? 'bg-jc-chart-2' : 'bg-jc-chart-context')}
                  style={{ width: `calc((100% - 2.5rem) * ${percentOf(t.value, 0, max) / 100})` }}
                />
                <span className={VALUE_TEXT}>{t.display}</span>
              </span>
            </span>
          );
        })}
        {thin.map((t) => (
          <span key={t.code} className="flex items-center gap-2 text-xs text-jc-ink-secondary">
            <span className="size-3 shrink-0 rounded-jc-info bg-jc-elevated" />
            <span className="w-2/5 shrink-0 truncate">{t.name}</span>
            {tooFew}
          </span>
        ))}
      </div>
    </div>
  );
}
